import React, { type ChangeEvent, type FC, useState } from 'react';
import { useEmailAttachmentActions, useEmailAttachments } from '../../../hooks/api/EmailTemplate/useEmailAttachments';
import { getErrorMessages } from '../../../utility/team';
import {
  ALLOWED_DOC_TYPES, attachedBytes, EmailDocKind, EmailDocument, emailDocumentUrl, formatFileSize,
} from '../../../utility/listingEmail';

const secondaryButton = 'rounded-md px-3 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50';
const linkButton = 'text-sm text-indigo-600 hover:underline';

const UploadButton: FC<{ label: string; kind: EmailDocKind; busy: boolean; onFile: (file: File, kind: EmailDocKind) => void }> = ({ label, kind, busy, onFile }) => {
  const handle = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // so choosing the same file again still fires
    if (file) onFile(file, kind);
  };
  return (
    <label className={`${secondaryButton} inline-block cursor-pointer ${busy ? 'opacity-50' : ''}`}>
      {busy ? 'Uploading...' : label}
      <input type='file' className='sr-only' accept={ALLOWED_DOC_TYPES} disabled={busy} aria-label={label} onChange={handle} />
    </label>
  );
};

const DocumentRow: FC<{ doc: EmailDocument; onToggle: (doc: EmailDocument) => void; onRename: (doc: EmailDocument, name: string) => void; onRemove: (doc: EmailDocument) => void }> =
  ({ doc, onToggle, onRename, onRemove }) => {
    const [ name, setName ] = useState(doc.name);
    return (
      <li className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3'>
        <div className='min-w-0 flex-1'>
          <input
            aria-label={`Name for ${doc.filename}`}
            className='block w-full rounded-md border-0 py-1 text-sm font-medium text-gray-900 ring-1 ring-inset ring-transparent hover:ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600'
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => { if (name.trim() && name !== doc.name) onRename(doc, name.trim()); else setName(doc.name); }}
          />
          <p className='text-xs text-gray-500'>{doc.filename} - {formatFileSize(doc.byte_size)}</p>
        </div>
        <label className='flex items-center gap-x-2 text-sm text-gray-700'>
          <input type='checkbox' checked={doc.attach_by_default} aria-label={`Attach ${doc.name} to every email`} onChange={() => onToggle(doc)} />
          Attach to every email
        </label>
        <a className={linkButton} href={emailDocumentUrl(doc.id)} target='_blank' rel='noreferrer'>View</a>
        <button type='button' className='text-sm text-gray-500 hover:text-red-600' onClick={() => onRemove(doc)}>Remove</button>
      </li>
    );
  };

// Documents added to every listing-agent email along with the contract: a proof of funds slot of its
// own plus any other documents (pre-approval letter, lender letter...). Only you can see these files.
const EmailDocumentsSection: FC = () => {
  const { documents, maxAdditional, maxMb, isLoading, error } = useEmailAttachments();
  const { upload, update, remove } = useEmailAttachmentActions();

  const proof = documents.find((d) => d.kind === 'proof_of_funds');
  const additional = documents.filter((d) => d.kind === 'additional');
  const atLimit = additional.length >= maxAdditional;

  const handleFile = (file: File, kind: EmailDocKind) => {
    upload.reset();
    upload.mutate({ file, kind, name: kind === 'proof_of_funds' ? 'Proof of funds' : undefined });
  };
  const handleRemove = (doc: EmailDocument) => {
    if (window.confirm(`Remove "${doc.name}"? It will no longer be attached to your emails.`)) remove.mutate(doc.id);
  };

  const failure = upload.error || update.error || remove.error;

  return (
    <section className='mt-10 max-w-3xl' aria-labelledby='email-docs-heading'>
      <h2 id='email-docs-heading' className='text-base font-semibold leading-6 text-gray-900'>Documents to send with every offer</h2>
      <p className='mt-2 text-sm text-gray-700'>
        These are attached to each listing-agent email after the contract, so you don't have to add them by hand.
        PDF, PNG, JPG or Word (.docx), up to {maxMb} MB each. Only you can see these files.
      </p>

      {isLoading && <p className='mt-4 text-sm text-gray-600'>Loading...</p>}
      {error && <p className='mt-4 text-sm text-red-600'>{getErrorMessages(error)[0]}</p>}

      {!isLoading && !error && (
        <>
          <div className='mt-4 rounded-md bg-gray-50 p-4' data-tag='proof-of-funds'>
            <h3 className='text-sm font-semibold text-gray-900'>Proof of funds</h3>
            {proof ? (
              <ul className='divide-y divide-gray-200'>
                <DocumentRow
                  doc={proof}
                  onToggle={(d) => update.mutate({ id: d.id, attach_by_default: !d.attach_by_default })}
                  onRename={(d, name) => update.mutate({ id: d.id, name })}
                  onRemove={handleRemove}
                />
              </ul>
            ) : (
              <p className='mt-1 text-sm text-gray-500'>None uploaded yet.</p>
            )}
            <div className='mt-2'>
              <UploadButton label={proof ? 'Replace proof of funds' : 'Upload proof of funds'} kind='proof_of_funds' busy={upload.isPending} onFile={handleFile} />
            </div>
          </div>

          <div className='mt-4'>
            <h3 className='text-sm font-semibold text-gray-900'>Other documents</h3>
            <ul className='divide-y divide-gray-100'>
              {additional.length === 0 && <li className='py-2 text-sm text-gray-500'>None uploaded yet.</li>}
              {additional.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  onToggle={(d) => update.mutate({ id: d.id, attach_by_default: !d.attach_by_default })}
                  onRename={(d, name) => update.mutate({ id: d.id, name })}
                  onRemove={handleRemove}
                />
              ))}
            </ul>
            <div className='mt-2'>
              <UploadButton label='Add a document' kind='additional' busy={upload.isPending || atLimit} onFile={handleFile} />
              {atLimit && <span className='ml-3 text-sm text-gray-500'>You can keep up to {maxAdditional} additional documents.</span>}
            </div>
          </div>

          {documents.length > 0 && (
            <p className='mt-4 text-xs text-gray-500'>
              Attached to every email: {formatFileSize(attachedBytes(documents))} plus the contract. Emails are limited to 20 MB in total.
            </p>
          )}
          {failure && (
            <ul role='alert' className='mt-2 text-sm text-red-600'>
              {getErrorMessages(failure).map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

export default EmailDocumentsSection;
